import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { perfilAcessoService, PerfilAcessoDetalhes } from '../../services/perfilAcesso';

interface Menu {
  id: string;
  desMenu: string;
}

interface PerfilAcessoData {
  id?: string;
  name: string;
  menus: string[];
}

const PerfilAcessoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<PerfilAcessoData>({
    name: '',
    menus: [],
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Buscar menus disponíveis
  const { data: menus = [], isLoading: menusLoading } = useQuery({
    queryKey: ['menusSelect'],
    queryFn: perfilAcessoService.getMenusSelect,
  });

  // Buscar dados do perfil para edição
  const { data: perfilData, isLoading: perfilLoading } = useQuery({
    queryKey: ['perfilAcesso', id],
    queryFn: () => perfilAcessoService.getById(id!),
    enabled: isEditing && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: PerfilAcessoData) => 
      perfilAcessoService.salvar({
        idRole: data.id,
        perfil: data.name,
        menus: data.menus,
      }),
    onSuccess: () => {
      // Invalidar o cache para forçar atualização da tabela e da visualização
      queryClient.invalidateQueries({ queryKey: ['perfisAcesso'] });
      queryClient.invalidateQueries({ queryKey: ['perfilAcesso'] });
      navigate('/perfis-acesso');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar perfil de acesso:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Erro ao salvar perfil de acesso' 
      });
    },
  });

  // Carregar dados do perfil quando estiver editando
  useEffect(() => {
    if (isEditing && perfilData) {
      const menusIds = perfilData.menus?.map(menu => menu.idMenu.toString()) || [];
      
      setFormData({
        id: perfilData.idRole,
        name: perfilData.role || '',
        menus: menusIds,
      });
    }
  }, [isEditing, perfilData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome do perfil é obrigatório';
    }

    if (formData.name.length > 50) {
      newErrors.name = 'Nome do perfil deve ter no máximo 50 caracteres';
    }

    if (formData.menus.length === 0) {
      newErrors.menus = 'Selecione pelo menos um menu';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleMenuChange = (menuId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      menus: checked 
        ? [...prev.menus, menuId]
        : prev.menus.filter(id => id !== menuId)
    }));
  };

  const handleBack = () => {
    navigate('/perfis-acesso');
  };

  if (menusLoading || (isEditing && perfilLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Voltar
        </Button>
        <Typography variant="h4" component="h1">
          {isEditing ? 'Editar Perfil de Acesso' : 'Novo Perfil de Acesso'}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <TextField
                  fullWidth
                  label="Nome do Perfil"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  error={!!errors.name}
                  helperText={errors.name}
                  slotProps={{
                    htmlInput: { maxLength: 50 }
                  }}
                  required
                />
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Menus Disponíveis
                </Typography>
                <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                  {menus.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      Nenhum menu disponível
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                      {menus.map((menu: Menu) => {
                        // Converter menu.id para string para comparar com formData.menus
                        const menuIdStr = menu.id.toString();
                        const isChecked = formData.menus.some(menuId => menuId === menuIdStr);
                        
                        return (
                          <FormControlLabel
                            key={`${menu.id}-${formData.menus.join('-')}`}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => handleMenuChange(menuIdStr, e.target.checked)}
                              />
                            }
                            label={menu.desMenu}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Paper>
                {errors.menus && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.menus}
                  </Typography>
                )}
              </Box>

              {errors.submit && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.submit}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saveMutation.isPending}
                  sx={{ minWidth: 120 }}
                >
                  {saveMutation.isPending ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/perfis-acesso')}
                  sx={{ minWidth: 120 }}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerfilAcessoForm;
